<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import moment from 'moment';
import ReportsAPI from 'dashboard/api/reports';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import ReportFilterSelector from './components/FilterSelector.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import PhoneReportsDownloadDialog from './PhoneReportsDownloadDialog.vue';

const { t } = useI18n();

const perPage = 20;
const cdrs = ref([]);
const currentPage = ref(1);
const totalCount = ref(0);
const loading = ref(false);
const isBulkDownloading = ref(false);
const expandedRecordingPlayers = ref({});
const selectedCdrIds = ref([]);
const downloadDialogRef = ref(null);
const filters = ref({
  from: null,
  to: null,
  agentIds: [],
  selectedAgents: [],
});

const columns = computed(() => [
  { key: 'select', title: t('PHONE_REPORTS.TABLE.HEADER.SELECT') },
  { key: 'origin', title: t('PHONE_REPORTS.TABLE.HEADER.ORIGIN') },
  { key: 'agent', title: t('PHONE_REPORTS.TABLE.HEADER.AGENT') },
  { key: 'destination', title: t('PHONE_REPORTS.TABLE.HEADER.DESTINATION') },
  {
    key: 'transferSourceNumber',
    title: t('PHONE_REPORTS.TABLE.HEADER.TRANSFER_SOURCE_NUMBER'),
  },
  { key: 'duration', title: t('PHONE_REPORTS.TABLE.HEADER.DURATION') },
  { key: 'start', title: t('PHONE_REPORTS.TABLE.HEADER.START') },
  { key: 'answer', title: t('PHONE_REPORTS.TABLE.HEADER.ANSWER') },
  { key: 'end', title: t('PHONE_REPORTS.TABLE.HEADER.END') },
  { key: 'direction', title: t('PHONE_REPORTS.TABLE.HEADER.DIRECTION') },
  { key: 'terminated', title: t('PHONE_REPORTS.TABLE.HEADER.TERMINATED') },
  { key: 'recordings', title: t('PHONE_REPORTS.TABLE.HEADER.RECORDINGS') },
]);

const formatDateBR = utcString => {
  if (!utcString) return '';

  return moment.utc(utcString).utcOffset(-180).format('DD/MM/YYYY HH:mm:ss');
};

const formatSeconds = value => {
  const numericValue = Number.parseFloat(value);
  if (!Number.isFinite(numericValue)) {
    return '0';
  }

  return Math.max(0, Math.floor(numericValue)).toString();
};

const directionLabel = direction => {
  if (direction === 'inbound') return t('PHONE_REPORTS.DIRECTION.INBOUND');
  if (direction === 'outbound') return t('PHONE_REPORTS.DIRECTION.OUTBOUND');

  return direction || '';
};

const terminatedBy = cdr => {
  if (cdr.Terminate === 'us') return cdr.Src;
  if (cdr.Terminate === 'them') return cdr.Dst;

  return cdr.Terminate || '';
};

const normalizeDialableNumber = value => {
  const rawValue = String(value || '').trim();
  if (!rawValue) {
    return '';
  }

  const normalizedValue = rawValue.replace(/[^\d+*#]/g, '');
  const digitCount = (normalizedValue.match(/\d/g) || []).length;

  return digitCount ? normalizedValue : '';
};

const sourceNumber = cdr =>
  normalizeDialableNumber(cdr?.SourceNumber) ||
  normalizeDialableNumber(cdr?.Src) ||
  '';

const destinationNumber = cdr =>
  normalizeDialableNumber(cdr?.DestinationNumber) ||
  normalizeDialableNumber(cdr?.Dst) ||
  '';

const transferSourceNumber = cdr => {
  const transfers = Array.isArray(cdr?.Transfers) ? cdr.Transfers : [];
  const values = [
    ...new Set(
      transfers
        .map(transfer => normalizeDialableNumber(transfer?.from))
        .filter(Boolean)
    ),
  ];

  return values.join(', ');
};

const availableRecordings = cdr =>
  (Array.isArray(cdr?.Recordings) ? cdr.Recordings : []).filter(
    recording => recording?.DownloadUrl
  );

const downloadableCdrIds = computed(() =>
  cdrs.value
    .filter(cdr => availableRecordings(cdr).length && cdr?.CdrId)
    .map(cdr => cdr.CdrId)
);

const selectedDownloadableCdrIds = computed(() =>
  selectedCdrIds.value.filter(cdrId => downloadableCdrIds.value.includes(cdrId))
);

const recordingKey = (cdr, recording, index) =>
  `${cdr?.CdrId || cdr?.SessionId || 'recording'}-${recording?.uID || index}`;

const isPlayerExpanded = key => !!expandedRecordingPlayers.value[key];

const toggleRecordingPlayer = key => {
  expandedRecordingPlayers.value = {
    ...expandedRecordingPlayers.value,
    [key]: !expandedRecordingPlayers.value[key],
  };
};

const recordingContentType = recording =>
  String(recording?.ContentType || '')
    .split(';')[0]
    .toLowerCase();

const isAudioRecording = recording => {
  const contentType = recordingContentType(recording);
  if (contentType.startsWith('audio/')) {
    return true;
  }

  return /\.(mp3|wav|ogg|m4a|aac|webm)(?:$|[?#])/i.test(
    recording?.DownloadUrl || ''
  );
};

const isVideoRecording = recording => {
  const contentType = recordingContentType(recording);
  if (contentType.startsWith('video/')) {
    return true;
  }

  return /\.(mp4|mov|webm)(?:$|[?#])/i.test(recording?.DownloadUrl || '');
};

const sanitizeFilenamePart = (value, fallback = 'gravacao') => {
  const normalizedValue = String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^0-9A-Za-z]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();

  return normalizedValue || fallback;
};

const recordingExtension = recording => {
  const contentType = recordingContentType(recording);
  const extensionByType = {
    'audio/mpeg': 'mp3',
    'audio/mp3': 'mp3',
    'audio/ogg': 'ogg',
    'audio/wav': 'wav',
    'audio/x-wav': 'wav',
    'audio/webm': 'webm',
    'video/mp4': 'mp4',
    'video/webm': 'webm',
  };

  if (extensionByType[contentType]) {
    return extensionByType[contentType];
  }

  const matchedExtension = recording?.DownloadUrl?.match(
    /\.([a-z0-9]+)(?:$|[?#])/i
  );

  return matchedExtension?.[1]?.toLowerCase() || 'webm';
};

const recordingTimestamp = cdr => {
  const sourceDate = cdr?.ItemDate || cdr?.CallAnswer || cdr?.CallEnd;
  if (!sourceDate) {
    return 'sem-data';
  }

  return moment.utc(sourceDate).utcOffset(-180).format('YYYY-MM-DD_HH-mm-ss');
};

const recordingDownloadFilename = (cdr, recording, index, recordingsCount) => {
  const destination = sanitizeFilenamePart(cdr?.Dst || cdr?.Src, 'destino');
  const timestamp = recordingTimestamp(cdr);
  const extension = recordingExtension(recording);
  const suffix = recordingsCount > 1 ? `-${index + 1}` : '';

  return `gravacao-${destination}-${timestamp}${suffix}.${extension}`;
};

const formatRowValue = (cdr, key) => {
  switch (key) {
    case 'origin':
      return sourceNumber(cdr);
    case 'agent':
      return cdr.ChatwootAgentName || cdr.Src || '';
    case 'destination':
      return destinationNumber(cdr);
    case 'transferSourceNumber':
      return transferSourceNumber(cdr);
    case 'duration':
      return formatSeconds(cdr.Billsec);
    case 'start':
      return formatDateBR(cdr.ItemDate);
    case 'answer':
      return cdr.CallAnswer
        ? formatDateBR(cdr.CallAnswer)
        : t('PHONE_REPORTS.ANSWER_NOT_AVAILABLE');
    case 'end':
      return formatDateBR(cdr.CallEnd);
    case 'direction':
      return directionLabel(cdr.CallDirection);
    case 'ring':
      return formatSeconds(cdr.RingTime);
    case 'terminated':
      return terminatedBy(cdr);
    default:
      return cdr[key] ?? '';
  }
};

const upsertCdrRow = cdr => {
  if (!cdr?.CdrId) {
    return;
  }

  const existingIndex = cdrs.value.findIndex(
    item => item?.CdrId && item.CdrId === cdr.CdrId
  );

  if (existingIndex >= 0) {
    cdrs.value = cdrs.value.map(item => (item.CdrId === cdr.CdrId ? cdr : item));
    return;
  }

  cdrs.value = [cdr, ...cdrs.value];
  totalCount.value += 1;

  if (cdrs.value.length > perPage) {
    cdrs.value = cdrs.value.slice(0, perPage);
  }
};

const resetSelection = () => {
  selectedCdrIds.value = [];
};

const toggleCdrSelection = cdrId => {
  if (!cdrId) {
    return;
  }

  if (selectedCdrIds.value.includes(cdrId)) {
    selectedCdrIds.value = selectedCdrIds.value.filter(id => id !== cdrId);
    return;
  }

  selectedCdrIds.value = [...selectedCdrIds.value, cdrId];
};

const loadCDRs = async () => {
  loading.value = true;

  try {
    const params = {
      page: currentPage.value,
      perPage,
      from: filters.value.from,
      to: filters.value.to,
      agentIds: filters.value.agentIds,
    };

    const response = await ReportsAPI.getPhoneReports(params);
    const data = response.data || {};
    cdrs.value = data.cdrs || [];
    totalCount.value = data.pagination?.total_count || 0;
    expandedRecordingPlayers.value = {};
    resetSelection();
  } catch {
    // Happy path only.
  } finally {
    loading.value = false;
  }
};

const handlePageChange = page => {
  currentPage.value = page;
  loadCDRs();
};

const onFilterChange = ({ from, to, selectedAgents }) => {
  filters.value = {
    from,
    to,
    agentIds: (selectedAgents || []).map(agent => agent.id),
    selectedAgents: selectedAgents || [],
  };
  currentPage.value = 1;
  loadCDRs();
};

const onPhoneCdrUpserted = event => {
  const cdr = event?.detail?.cdr;
  if (!cdr) {
    return;
  }

  if (filters.value.agentIds.length || filters.value.from || filters.value.to || currentPage.value !== 1) {
    loadCDRs();
    return;
  }

  upsertCdrRow(cdr);
};

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
};

const archiveFilenameFromHeaders = headers => {
  const contentDisposition = headers?.['content-disposition'] || '';
  const encodedMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (encodedMatch?.[1]) {
    return decodeURIComponent(encodedMatch[1]);
  }

  const plainMatch = contentDisposition.match(/filename=\"?([^\";]+)\"?/i);
  if (plainMatch?.[1]) {
    return plainMatch[1];
  }

  return `gravacoes-chamadas-${moment().format('YYYY-MM-DD_HH-mm-ss')}.tar.gz`;
};

const openDownloadDialog = () => {
  downloadDialogRef.value?.open();
};

const onDownloadDialogConfirm = async ({
  useSelectedCallsOnly,
  from,
  to,
  agentIds,
}) => {
  if (isBulkDownloading.value) {
    return;
  }

  isBulkDownloading.value = true;

  try {
    const response = await ReportsAPI.downloadPhoneRecordings({
      from,
      to,
      agentIds,
      cdrIds: useSelectedCallsOnly ? selectedDownloadableCdrIds.value : [],
    });

    const fileName = archiveFilenameFromHeaders(response.headers);
    const blob = new Blob([response.data], {
      type: response.headers?.['content-type'] || 'application/gzip',
    });

    downloadBlob(blob, fileName);
    downloadDialogRef.value?.close();
  } catch {
    useAlert(t('PHONE_REPORTS.RECORDINGS.DOWNLOAD_ALL_FAILED'));
  } finally {
    isBulkDownloading.value = false;
  }
};

onMounted(() => {
  loadCDRs();
  window.addEventListener('phone-cdr-upserted', onPhoneCdrUpserted);
});

onBeforeUnmount(() => {
  window.removeEventListener('phone-cdr-upserted', onPhoneCdrUpserted);
});
</script>

<template>
  <div class="flex-1 space-y-4 p-4">
    <div class="flex flex-col gap-3 xl:flex-row xl:items-start">
      <div class="min-w-0 flex-1">
        <ReportFilterSelector
          :show-agents-filter="true"
          :show-business-hours-switch="false"
          @filter-change="onFilterChange"
        />
      </div>
      <div class="shrink-0 xl:pt-[2px]">
        <Button
          slate
          sm
          icon="i-ph-download-simple"
          :label="t('PHONE_REPORTS.RECORDINGS.DOWNLOAD_ALL')"
          :disabled="loading"
          @click="openDownloadDialog"
        />
      </div>
    </div>

    <div class="overflow-hidden rounded-xl border border-n-strong bg-n-background">
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-n-strong">
          <thead class="bg-n-alpha-2">
            <tr>
              <th
                v-for="column in columns"
                :key="column.key"
                :class="[
                  'px-4 py-3 text-xs font-semibold uppercase tracking-wide text-n-slate-11',
                  column.key === 'select'
                    ? 'w-12 text-center'
                    : 'whitespace-nowrap text-left',
                ]"
              >
                <template v-if="column.key === 'select'">
                  <span class="sr-only">{{ t('PHONE_REPORTS.RECORDINGS.SELECT_CALL') }}</span>
                </template>
                <template v-else>
                  {{ column.title }}
                </template>
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-strong bg-n-background">
            <tr v-if="loading">
              <td
                :colspan="columns.length"
                class="px-4 py-6 text-center text-sm text-n-slate-11"
              >
                {{ t('GENERAL.LOADING') }}
              </td>
            </tr>
            <tr v-else-if="!cdrs.length">
              <td
                :colspan="columns.length"
                class="px-4 py-8 text-center text-sm text-n-slate-11"
              >
                {{ t('PHONE_REPORTS.NO_RECORDS') }}
              </td>
            </tr>
            <template v-else>
              <tr
                v-for="(cdr, rowIndex) in cdrs"
                :key="cdr.CallId || `${cdr.Src}-${cdr.ItemDate}-${rowIndex}`"
                class="bg-n-background"
              >
                <td
                  v-for="column in columns"
                  :key="column.key"
                  :class="[
                    'px-4 py-3 text-sm text-n-slate-12 align-top',
                    column.key === 'select'
                      ? 'w-12 text-center'
                      : column.key === 'recordings'
                        ? 'min-w-[16rem]'
                        : 'whitespace-nowrap',
                  ]"
                >
                  <template v-if="column.key === 'select'">
                    <label
                      v-if="availableRecordings(cdr).length"
                      class="inline-flex cursor-pointer justify-center"
                    >
                      <span class="sr-only">{{ t('PHONE_REPORTS.RECORDINGS.SELECT_CALL') }}</span>
                      <Checkbox
                        :model-value="selectedCdrIds.includes(cdr.CdrId)"
                        @change="() => toggleCdrSelection(cdr.CdrId)"
                      />
                    </label>
                  </template>
                  <template v-else-if="column.key === 'recordings'">
                    <div
                      v-if="availableRecordings(cdr).length"
                      class="flex flex-col gap-3"
                    >
                      <div
                        v-for="(recording, recordingIndex) in availableRecordings(cdr)"
                        :key="recordingKey(cdr, recording, recordingIndex)"
                        class="rounded-lg border border-n-strong bg-n-alpha-2 p-3"
                      >
                        <div class="flex items-center gap-2">
                          <span
                            v-if="availableRecordings(cdr).length > 1"
                            class="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-n-brand/10 px-2 text-xs font-semibold text-n-brand"
                          >
                            {{ recordingIndex + 1 }}
                          </span>
                          <button
                            type="button"
                            class="inline-flex items-center gap-2 rounded-md border border-n-strong bg-n-background px-3 py-1.5 text-xs font-medium text-n-slate-12 transition hover:border-n-brand hover:text-n-brand"
                            @click="
                              toggleRecordingPlayer(
                                recordingKey(cdr, recording, recordingIndex)
                              )
                            "
                          >
                            <span
                              :class="
                                isPlayerExpanded(
                                  recordingKey(cdr, recording, recordingIndex)
                                )
                                  ? 'i-ri-eye-off-line'
                                  : 'i-ri-play-circle-line'
                              "
                            />
                            {{
                              isPlayerExpanded(
                                recordingKey(cdr, recording, recordingIndex)
                              )
                                ? t('PHONE_REPORTS.RECORDINGS.HIDE')
                                : t('PHONE_REPORTS.RECORDINGS.PLAY')
                            }}
                          </button>
                          <a
                            class="inline-flex items-center gap-2 rounded-md border border-n-strong bg-n-background px-3 py-1.5 text-xs font-medium text-n-slate-12 transition hover:border-n-brand hover:text-n-brand"
                            :href="recording.DownloadUrl"
                            :download="
                              recordingDownloadFilename(
                                cdr,
                                recording,
                                recordingIndex,
                                availableRecordings(cdr).length
                              )
                            "
                            target="_blank"
                            rel="noreferrer"
                          >
                            <span class="i-ri-download-2-line" />
                            {{ t('PHONE_REPORTS.RECORDINGS.DOWNLOAD') }}
                          </a>
                        </div>

                        <audio
                          v-if="
                            isPlayerExpanded(
                              recordingKey(cdr, recording, recordingIndex)
                            ) && isAudioRecording(recording)
                          "
                          class="mt-3 w-full"
                          controls
                          preload="none"
                          :src="recording.DownloadUrl"
                        />

                        <video
                          v-else-if="
                            isPlayerExpanded(
                              recordingKey(cdr, recording, recordingIndex)
                            ) && isVideoRecording(recording)
                          "
                          class="mt-3 w-full rounded-lg border border-n-strong"
                          controls
                          preload="none"
                          :src="recording.DownloadUrl"
                        />
                      </div>
                    </div>
                    <span v-else class="text-n-slate-10">---</span>
                  </template>
                  <template v-else>
                    {{ formatRowValue(cdr, column.key) }}
                  </template>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <div
        v-if="totalCount > perPage"
        class="flex justify-end bg-n-alpha-2 px-4 py-4"
      >
        <PaginationFooter
          :current-page="currentPage"
          :total-items="totalCount"
          :items-per-page="perPage"
          @update:currentPage="handlePageChange"
        />
      </div>
    </div>
    <PhoneReportsDownloadDialog
      ref="downloadDialogRef"
      :initial-from="filters.from"
      :initial-to="filters.to"
      :initial-selected-agents="filters.selectedAgents"
      :selected-call-count="selectedDownloadableCdrIds.length"
      :is-downloading="isBulkDownloading"
      @download="onDownloadDialogConfirm"
    />
  </div>
</template>
